import type { Card, Suit } from '../../engine/card.js';
import { color } from '../../engine/card.js';
import type { GameState, Pile } from '../../engine/game-state.js';
import { topCard } from '../../engine/game-state.js';
import type { Move } from '../../engine/moves.js';
import type { SolitaireRules } from '../../engine/rules.js';

export const TABLEAU_IDS = ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8'] as const;
export const FOUNDATION_IDS = ['f-spades', 'f-hearts', 'f-diamonds', 'f-clubs'] as const;
export const CELL_IDS = ['c1', 'c2', 'c3', 'c4'] as const;

const FOUNDATION_SUIT: Record<(typeof FOUNDATION_IDS)[number], Suit> = {
  'f-spades': 'spades',
  'f-hearts': 'hearts',
  'f-diamonds': 'diamonds',
  'f-clubs': 'clubs',
};

function emptyPile(id: string, type: Pile['type']): Pile {
  return { id, type, cards: [] };
}

function setupFreeCell(deck: Card[]): GameState {
  const piles: Record<string, Pile> = {};
  for (const id of TABLEAU_IDS) piles[id] = emptyPile(id, 'tableau');
  for (const id of FOUNDATION_IDS) piles[id] = emptyPile(id, 'foundation');
  for (const id of CELL_IDS) piles[id] = emptyPile(id, 'cell');

  const d = deck.slice();
  for (let i = 0; i < d.length; i++) {
    const card = d[i]!;
    card.faceUp = true;
    const colIdx = i % TABLEAU_IDS.length;
    piles[TABLEAU_IDS[colIdx]!]!.cards.push(card);
  }

  return { piles, movesCount: 0 };
}

function isFoundationFor(pile: Pile, suit: Suit): boolean {
  return pile.type === 'foundation' && FOUNDATION_SUIT[pile.id as keyof typeof FOUNDATION_SUIT] === suit;
}

function canPlaceOnFoundation(pile: Pile, card: Card): boolean {
  if (!isFoundationFor(pile, card.suit)) return false;
  const top = topCard(pile);
  if (!top) return card.rank === 1;
  return card.rank === top.rank + 1;
}

function canPlaceOnTableau(pile: Pile, card: Card): boolean {
  if (pile.type !== 'tableau') return false;
  const top = topCard(pile);
  if (!top) return true;
  return color(card) !== color(top) && card.rank === top.rank - 1;
}

function canPlaceInCell(pile: Pile): boolean {
  return pile.type === 'cell' && pile.cards.length === 0;
}

export function isAltColorDescending(cards: Card[]): boolean {
  if (cards.length === 0) return false;
  for (let i = 1; i < cards.length; i++) {
    const a = cards[i - 1]!;
    const b = cards[i]!;
    if (color(a) === color(b)) return false;
    if (a.rank !== b.rank + 1) return false;
  }
  return true;
}

export function maxSupermoveSize(state: GameState, toEmptyTableau: boolean): number {
  let emptyCells = 0;
  let emptyTableaus = 0;
  for (const pile of Object.values(state.piles)) {
    if (pile.type === 'cell' && pile.cards.length === 0) emptyCells++;
    if (pile.type === 'tableau' && pile.cards.length === 0) emptyTableaus++;
  }
  if (toEmptyTableau) emptyTableaus = Math.max(0, emptyTableaus - 1);
  return (1 + emptyCells) * 2 ** emptyTableaus;
}

export const freecellRules: SolitaireRules = {
  id: 'freecell',

  setup(deck) {
    return setupFreeCell(deck);
  },

  canMove(state, move: Move) {
    const from = state.piles[move.from];
    const to = state.piles[move.to];
    if (!from || !to) return false;
    if (from === to) return false;

    const idx = from.cards.findIndex((c) => c.id === move.cardId);
    if (idx === -1) return false;
    const moving = from.cards.slice(idx);
    if (moving.length === 0) return false;
    const head = moving[0]!;
    if (!head.faceUp) return false;

    if (to.type === 'foundation') {
      return moving.length === 1 && canPlaceOnFoundation(to, head);
    }
    if (to.type === 'cell') {
      return moving.length === 1 && canPlaceInCell(to);
    }
    if (to.type === 'tableau') {
      if (!isAltColorDescending(moving)) return false;
      if (!canPlaceOnTableau(to, head)) return false;
      const max = maxSupermoveSize(state, to.cards.length === 0);
      return moving.length <= max;
    }
    return false;
  },

  isWon(state) {
    return FOUNDATION_IDS.every((id) => state.piles[id]!.cards.length === 13);
  },
};

export function hasAnyLegalMove(state: GameState): boolean {
  const pileIds = Object.keys(state.piles);
  for (const fromPile of Object.values(state.piles)) {
    const card = topCard(fromPile);
    if (!card) continue;
    for (const toId of pileIds) {
      if (toId === fromPile.id) continue;
      if (freecellRules.canMove(state, { from: fromPile.id, to: toId, cardId: card.id })) {
        return true;
      }
    }
  }
  return false;
}
